import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { Bid } from 'cargo-chief-commons';
import { BidsStore, QuoteStatusFilter } from '../state/bids.store';
import { BidListConfig, BidFilters } from '../models/bid-list-config.interface';

@Injectable({ providedIn: 'root' })
export class BidsFacade {
	// Expose store selectors
	public readonly bids$ = this.bidsStore.bids$;
	public readonly loading$ = this.bidsStore.loading$;
	public readonly totalElements$ = this.bidsStore.totalElements$;
	public readonly hasNext$ = this.bidsStore.hasNext$;
	public readonly pageNumber$ = this.bidsStore.pageNumber$;
	public readonly pageSize$ = this.bidsStore.pageSize$;
	public readonly initialBidsListLoaded$ = this.bidsStore.initialBidsListLoaded$;
	public readonly offerUpdatesAvailable$ = this.bidsStore.offerUpdatesAvailable$;
	public readonly currentConfig$ = this.bidsStore.currentConfig$;
	public readonly allTabs$ = this.bidsStore.allTabs$;
	public readonly activeTab$ = this.bidsStore.activeTab$;
	public readonly userSortAndFilters$ = this.bidsStore.userSortAndFilters$;
	public readonly customTabDeleted$ = this.bidsStore.customTabDeleted$;
	public readonly quoteStatusFilter$ = this.bidsStore.quoteStatusFilter$;

	constructor(private readonly bidsStore: BidsStore) {}

	// Public methods for uniqueBidIds access
	getUniqueBidIds(): string[] {
		return this.bidsStore.getUniqueBidIds();
	}

	addUniqueBidId(bidId: string): void {
		this.bidsStore.addUniqueBidId(bidId);
	}

	hasUniqueBidId(bidId: string): boolean {
		return this.bidsStore.hasUniqueBidId(bidId);
	}

	getUniqueBidIdsCount(): number {
		return this.bidsStore.getUniqueBidIdsCount();
	}

	// High-level operations
	loadBidsForQuote(quoteId: string, pageSize?: number): void {
		const config: BidListConfig = {
			context: 'quote',
			quoteId,
			pageSize: pageSize || 15,
		};
		this.bidsStore.loadBids(config);
	}

	loadBidsForCarrier(accountId: string, filters?: BidFilters, pageSize?: number): void {
		const config: BidListConfig = {
			context: 'carrier',
			accountId,
			filters,
			pageSize: pageSize || 25,
		};
		this.bidsStore.loadBids(config);
	}

	loadGlobalBids(filters?: BidFilters, pageSize?: number): void {
		const config: BidListConfig = {
			context: 'global',
			filters,
			pageSize: pageSize || 25,
		};
		this.bidsStore.loadBids(config);
	}

	loadBids(config: BidListConfig): void {
		this.bidsStore.loadBids(config);
	}

	updateBid(bidId: string, changes: Partial<Bid>, quoteLane?: string): void {
		this.bidsStore.updateBid({ id: bidId, changes, quoteLane });
	}

	updateAllBids(changes: Partial<Bid>): void {
		this.bidsStore.updateAllBids(changes);
	}

	setPageNumber(pageNumber: number): void {
		this.bidsStore.setPageNumber(pageNumber);
	}

	setOfferUpdatesAvailable(available: boolean): void {
		this.bidsStore.setOfferUpdatesAvailable(available);
	}

	updateTotalBidsCount(config: BidListConfig): void {
		this.bidsStore.updateTotalBidsCount(config);
	}

	clearBids(): void {
		this.bidsStore.clear();
	}

	resetBidsState(): void {
		this.bidsStore.setInitialStateForBidsList();
	}

	setCustomTabId(customTabId: number) {
		this.bidsStore.setCustomTabId(customTabId);
	}

	deleteCustomTab(customTabId: number) {
		this.bidsStore.deleteCustomTab(of(customTabId));
	}

	getCustomTabId() {
		return this.bidsStore.customTabId;
	}

	setActiveTab(tab: string) {
		this.bidsStore.setActiveTab(tab);
	}

	// CN-8620: set the quote bid list status filter (is / is not).
	setQuoteStatusFilter(filter: QuoteStatusFilter) {
		this.bidsStore.setQuoteStatusFilter(filter);
	}
}
